import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  Box,
  Flex,
  Link,
  SimpleGrid,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import { BentoLayoutSize } from 'common/bento/lib/layout';
import useLayoutSizeQuery from 'common/bento/lib/layout/useLayoutSizeQuery';
import zodLocales from 'common/zod/locales';
import { zipCodeSchema } from 'common/zod/schemas';
import * as Form from 'components/_core/form';
import IconButton from 'components/_core/IconButton';
import SunshineIcon from 'components/_core/SunshineIcon';
import OBBackButton from 'features/Onboarding/components/OBBackButton';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import OBStickyButton from 'features/Onboarding/components/OBStickyButton/OBStickyButton';

import AddressSearchInput from '../../../../components/AddressSearchInput';
import Header from '../../../../components/Header';
import SelectOnlyFrance from '../../../../components/SelectOnlyFrance';
import { Context } from '../../machine/model';
import locales from './locales';

const MAX_STREET_CHARACTERS_LENGTH = 45;

const identityInfoSchema = z.object({
  city: z.string().min(1, zodLocales.errors.required),
  country: z.string().min(1, zodLocales.errors.required),
  legalName: z.string().min(1, zodLocales.errors.required),
  street: z
    .string()
    .min(1, zodLocales.errors.required)
    .max(
      MAX_STREET_CHARACTERS_LENGTH,
      locales.formatString(
        locales.inputs.street.errors.limitedLength,
        MAX_STREET_CHARACTERS_LENGTH,
      ),
    ),
  streetAddition: z.string().optional().default(''),
  tradeName: z.string().optional().nullable().default(null),
  zip: zipCodeSchema,
});

export type IdentityInfoPayload = z.infer<typeof identityInfoSchema>;

interface IdentityInfoEditProps {
  company: Context['companyProfile'];
  isEditing: boolean;
  onContinue: SubmitHandler<IdentityInfoPayload>;
}

const IdentityInfoEdit = asBentoPage<IdentityInfoEditProps>(
  ({ company, isEditing, Layout, onContinue, onPrev }) => {
    const {
      formState: { errors },
      getValues,
      handleSubmit,
      register,
      setFocus,
      setValue,
      trigger: validateForm,
    } = useForm<IdentityInfoPayload>({
      defaultValues: {
        city: company.city,
        country: company.country,
        legalName: company.legalName,
        street: company.street,
        streetAddition: company.streetAddition,
        tradeName: company.tradeName,
        zip: company.zip,
      },
      mode: company.street || company.legalName ? 'onChange' : 'onSubmit',
      resolver: zodResolver(identityInfoSchema),
    });

    const isDesktop = useLayoutSizeQuery(BentoLayoutSize.md);
    const [hasAddressComplement, setHasAddressComplement] = useState(
      Boolean(company.streetAddition),
    );
    const addressOverlay = useDisclosure();
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (company.legalName || company.street || company.city || company.zip) {
        validateForm();
      }
    }, [company, validateForm]);

    const onSubmit = handleSubmit((values) => {
      onContinue({
        ...values,
        // Always set the company's country to France when submitting
        country: 'FR',
      });
    });

    useLayoutEffect(() => {
      // Focus the streetAddition field when you just clicked on the add button
      // We can't do the focus in the callback of the button because the input is not displayed yet
      if (hasAddressComplement && !getValues('streetAddition')) {
        setFocus('streetAddition');
      }
    }, [hasAddressComplement, setFocus, getValues]);

    return (
      <Layout
        data-testid="identity-info-edit"
        header={<OBBackButton onClick={onPrev} />}
        nav={
          <OBNavigation
            isCollapsed={addressOverlay.isOpen ? true : undefined}
            onClose={addressOverlay.isOpen ? addressOverlay.onClose : undefined}
            onPrev={onPrev}
          />
        }
      >
        <Box ref={containerRef}>
          <Flex
            direction="column"
            display={addressOverlay.isOpen ? 'none' : undefined}
          >
            <Header title={locales.title[isEditing ? 'edit' : 'add']} />

            <Flex
              as="form"
              data-testid="business-address-edit-form"
              direction="column"
              height="full"
              justify="space-between"
              noValidate
              onSubmit={onSubmit}
            >
              <VStack alignItems="stretch" spacing="space-24">
                <Form.Field
                  error={errors.legalName?.message}
                  label={locales.inputs.legalName.label}
                >
                  <Form.Input
                    {...register('legalName')}
                    data-testid="legal-name-input"
                    required
                  />
                </Form.Field>

                <Form.Field
                  error={errors.tradeName?.message}
                  label={locales.inputs.tradeName.label}
                >
                  <Form.Input
                    {...register('tradeName')}
                    data-testid="trade-name-input"
                  />
                </Form.Field>

                <Box>
                  <Form.Field
                    error={errors.street?.message}
                    label={locales.inputs.street.label}
                  >
                    <AddressSearchInput
                      {...register('street')}
                      allowManualMode={!isDesktop}
                      data-testid="street-input"
                      isDesktop={isDesktop}
                      isOverlayOpen={addressOverlay.isOpen}
                      isRequired
                      label={locales.inputs.street.label}
                      onOverlayClose={addressOverlay.onClose}
                      onOverlayOpen={addressOverlay.onOpen}
                      onSelectAddress={(address) => {
                        const { city, street, streetAddition, zip } = address;
                        setValue('street', street);
                        setValue('city', city);
                        setValue('zip', zip);
                        setValue('streetAddition', streetAddition ?? '');
                        setHasAddressComplement(Boolean(streetAddition));
                      }}
                      overlayContainerRef={containerRef}
                      searchPlaceholder={locales.inputs.street.placeholder}
                    />
                  </Form.Field>

                  <Form.Field
                    display={hasAddressComplement ? undefined : 'none'}
                    error={errors.streetAddition?.message}
                    label={locales.inputs.streetAddition.label}
                    marginTop="space-24"
                  >
                    <Flex alignItems="center" gap="space-8">
                      <Form.Input
                        {...register('streetAddition')}
                        data-testid="streetAddition-input"
                        placeholder={locales.inputs.streetAddition.placeholder}
                      />
                      <IconButton
                        aria-label={locales.clear}
                        icon="trash"
                        onClick={() => {
                          setValue('streetAddition', '');
                          setHasAddressComplement(false);
                        }}
                        variant="inline-secondary"
                      />
                    </Flex>
                  </Form.Field>
                  <Link
                    alignItems="center"
                    display={hasAddressComplement ? 'none' : undefined}
                    flex="row"
                    marginTop="space-16"
                    onClick={() => {
                      setHasAddressComplement(true);
                    }}
                  >
                    <SunshineIcon
                      marginRight="space-12"
                      name="plus"
                      size="icon-13"
                    />
                    {locales.addStreetAddition}
                  </Link>
                </Box>
                <SimpleGrid columns={2} spacing="space-16">
                  <Form.Field
                    error={errors.zip?.message}
                    label={locales.inputs.zip.label}
                  >
                    <Form.Input
                      {...register('zip')}
                      data-testid="zip-input"
                      inputMode="numeric"
                      maxLength={5}
                      required
                    />
                  </Form.Field>

                  <Form.Field
                    error={errors.city?.message}
                    label={locales.inputs.city.label}
                  >
                    <Form.Input
                      {...register('city')}
                      data-testid="city-input"
                      required
                    />
                  </Form.Field>
                </SimpleGrid>
                <SelectOnlyFrance />
              </VStack>

              <OBStickyButton justifyContent="left" type="submit">
                {isEditing ? locales.save : locales.continue}
              </OBStickyButton>
            </Flex>
          </Flex>
        </Box>
      </Layout>
    );
  },
);

export default IdentityInfoEdit;
